import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { haptic } from '@/lib/tg'
import cicadaWhite from '@/assets/cicada_white.png'
import cicadaBlack from '@/assets/cicada_black.png'

const SEEN_KEY = 'cicada_onboarded_v1'

export function hasOnboarded(): boolean {
  try { return localStorage.getItem(SEEN_KEY) === '1' } catch { return true }
}
function markOnboarded() {
  try { localStorage.setItem(SEEN_KEY, '1') } catch { /* ignore */ }
}

type Step = { tag: string; title: string; body: string; icon: 'shield' | 'card' | 'bolt' | 'gift' }

const STEPS: Step[] = [
  {
    tag: 'ЦИКАДА',
    title: 'БЫСТРО\nИ ПРИВАТНО',
    body: 'Шифрованное VPN-соединение для любых сайтов и приложений. Стабильная скорость даже там, где сеть перегружена.',
    icon: 'shield',
  },
  {
    tag: '01',
    title: 'ПОДПИСКА\nНА ВЫБОР',
    body: '3 дня бесплатно. Понравится — тариф от 300 ₽/мес: 3 устройства в комплекте, без скрытых списаний.',
    icon: 'card',
  },
  {
    tag: '02',
    title: 'ВКЛЮЧИЛ —\nИ РАБОТАЕТ',
    body: 'Жмёшь «Подключить» — открывается INCY и сам добавляет VPN. Настроил один раз, дальше просто включаешь тумблер.',
    icon: 'bolt',
  },
  {
    tag: '03',
    title: 'ЗОВИ\nДРУЗЕЙ',
    body: 'Пригласи друга по своей ссылке — оба получите +7 дней бесплатно, когда он оформит тариф.',
    icon: 'gift',
  },
]

export function Onboarding({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const last = i === STEPS.length - 1
  const step = STEPS[i]

  const finish = () => {
    haptic('medium')
    markOnboarded()
    setLeaving(true)
    setTimeout(onDone, 280)
  }
  const next = () => {
    if (last) return finish()
    haptic('light')
    setI(i + 1)
  }
  const skip = () => { haptic('light'); finish() }

  return (
    <AnimatePresence>
      {!leaving && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col overflow-hidden"
          style={{ background: 'var(--color-bg)', paddingTop: 'calc(env(safe-area-inset-top) + 14px)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 18px)' }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: 18 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* cicada watermark */}
          <img src={cicadaBlack} alt="" className="only-light pointer-events-none select-none absolute -right-14 -top-6 w-[58%] max-w-[320px] opacity-[0.05]" />
          <img src={cicadaWhite} alt="" className="only-dark pointer-events-none select-none absolute -right-14 -top-6 w-[58%] max-w-[320px] opacity-[0.06]" />

          {/* top bar: progress + skip */}
          <div className="px-6 flex items-center justify-between shrink-0">
            <div className="flex gap-1.5">
              {STEPS.map((_, k) => (
                <div key={k} className="h-[3px] rounded-full transition-all duration-300"
                  style={{ width: k === i ? 22 : 7, background: k <= i ? 'var(--color-accent)' : 'var(--color-line)' }} />
              ))}
            </div>
            {!last && (
              <button onClick={skip} className="tap label" style={{ color: 'var(--color-muted)' }}>пропустить</button>
            )}
          </div>

          {/* content */}
          <div className="flex-1 min-h-0 flex flex-col justify-center px-6">
            <AnimatePresence mode="wait">
              <motion.div key={i}
                initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="mb-7"><StepIcon icon={step.icon} /></div>
                <div className="label mb-3" style={{ color: 'var(--color-accent)' }}>{step.tag}</div>
                <h1 className="display text-[40px] leading-[0.92] whitespace-pre-line">
                  {step.title}<span style={{ color: 'var(--color-accent)' }}>.</span>
                </h1>
                <p className="mt-5 text-[15px] leading-relaxed max-w-[19rem]" style={{ color: 'var(--color-muted)' }}>
                  {step.body}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* primary action */}
          <div className="px-6 shrink-0">
            <button onClick={next}
              className="tap w-full py-4 display text-[20px] flex items-center justify-center gap-3"
              style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)', borderRadius: 6 }}
            >
              {last ? 'НАЧАТЬ' : 'ДАЛЕЕ'}
              <span className="text-[18px] leading-none">▸</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function StepIcon({ icon }: { icon: Step['icon'] }) {
  const common = {
    width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  }
  return (
    <div className="size-[58px] rounded-[12px] flex items-center justify-center"
      style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)', border: '1px solid var(--color-line)' }}>
      {icon === 'shield' && <svg {...common}><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>}
      {icon === 'card' && <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3 9h18M7 14h4" /></svg>}
      {icon === 'bolt' && <svg {...common}><path d="M13 3L5 13h6l-1 8 8-10h-6z" /></svg>}
      {icon === 'gift' && <svg {...common}><rect x="4" y="9" width="16" height="11" rx="1.5" /><path d="M4 13h16M12 9v11M12 9c-1.5-3-5-3-5-.5 0 1 1 1.5 5 .5M12 9c1.5-3 5-3 5-.5 0 1-1 1.5-5 .5" /></svg>}
    </div>
  )
}
